import {
  Alert,
  Box,
  Icon,
  Radio,
  Text,
  ToggleButton,
  WizardStepRenderParams,
} from '@factorialco/deprecated-design-system'
import { BankingCardLimitPeriodEnum } from 'generated/resources/graphql'
import moment from 'moment/moment'

import DayPicker from 'modules/core/components/DayPicker'
import MoneyField from 'modules/core/components/MoneyField'
import SelectField from 'modules/core/components/SelectField'
import TextField from 'modules/core/components/TextField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { dateBackendToUI, dateToString, dateUIToBackend } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency from 'modules/core/lib/money'

import DialCodeSelector from 'modules/expenses/components/common/DialCodeSelector'
import type { WizardData } from 'modules/software_management/components/SoftwareWizard/types'
import { flexibilityOptions, getCardPeriodOptions } from 'modules/software_management/lib/cards'
import { useApplicationsContext } from 'modules/software_management/pages/ApplicationsPage'

export const SpendControlStep = ({ data, onDataChanged }: WizardStepRenderParams<WizardData>) => {
  const { accountProvidersAvailable, bankAccount } = useApplicationsContext()
  const {
    access: { locale },
  } = useCurrentSession()

  const {
    spendingAmount,
    spendingPeriod,
    budgetAmount,
    budgetFlexibility,
    birthDate,
    countryCode,
    phoneNumber,
    errors,
    spendType,
    currency,
    employee,
  } = data

  const maxDate = moment().subtract(18, 'years')

  useEffect(() => {
    track(trackingEvents.softwareManagement.goToSoftwareSetupWizardStep, {
      step: 2,
    })
  }, [])

  useEffect(() => {
    onDataChanged({ ...data, spendingAmount: calculateSpending() })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [budgetAmount, budgetFlexibility])

  const paymentTypes = [
    {
      description: i18n.t(
        'software_management.initial_setup.wizard.spend_control_form.manual.description'
      ),
      option: {
        label: i18n.t('software_management.initial_setup.wizard.spend_control_form.manual.label'),
        value: 'manual',
      },
      show: true,
    },
    {
      description: i18n.t(
        'software_management.initial_setup.wizard.spend_control_form.card.description'
      ),
      option: {
        label: i18n.t('software_management.initial_setup.wizard.spend_control_form.card.label'),
        value: 'card',
        disabled: !bankAccount,
      },
      show: accountProvidersAvailable,
    },
  ]

  const formattedAmount = MoneyWithCurrency({
    amount: spendingAmount || 0,
    locale,
    currency,
  })

  const setFlexibility = (value: number) => {
    const selected = budgetFlexibility == value ? undefined : value

    onDataChanged({ ...data, budgetFlexibility: selected })
  }

  const calculateSpending = () => {
    const multiplier = (budgetFlexibility || 0) / 100

    const amountInCents = Math.round(Number(budgetAmount) * (1 + multiplier) * 100)

    return amountInCents
  }

  const cardSpendType = spendType === 'card'
  const cardholder = employee.bankingCardholder
  const cardholderExists = !!cardholder
  const currentUserFullName = employee.fullName

  return (
    <Box gap="s64" flexDirection="row">
      <Box gap="s24" width="full">
        <Box gap="s8">
          <Text.Header2>
            {i18n.t('software_management.initial_setup.wizard.steps.spend_control.header')}
          </Text.Header2>

          <Text.Normal>
            {i18n.t('software_management.initial_setup.wizard.steps.spend_control.description')}
          </Text.Normal>
        </Box>

        <Box gap="s8" maxWidth="s600">
          {paymentTypes
            .filter(({ show }) => show)
            .map(({ option, description }) => (
              <Radio.Wrap key={option.value}>
                <Radio
                  name={'spend_type'}
                  value={data.spendType}
                  onChange={(value) => onDataChanged({ ...data, spendType: value })}
                  description={description}
                  option={option}
                />
              </Radio.Wrap>
            ))}
        </Box>

        {cardSpendType && (
          <Box gap="s64" flexDirection="row">
            <Box gap="s8" maxWidth="s600">
              <Box gap="s24" flexDirection="row">
                <MoneyField
                  mandatory
                  placeholder="0"
                  currency="EUR"
                  label={i18n.t('software_management.initial_setup.wizard.costs_form.budget')}
                  value={budgetAmount}
                  onChange={(value) => onDataChanged({ ...data, budgetAmount: value })}
                  readonly={!cardSpendType}
                  error={errors?.budgetAmount}
                />

                <SelectField
                  mandatory
                  label={i18n.t('software_management.initial_setup.wizard.costs_form.period')}
                  options={getCardPeriodOptions()}
                  value={spendingPeriod}
                  onChange={(value) => {
                    onDataChanged({
                      ...data,
                      // eslint-disable-next-line no-type-assertion/no-type-assertion
                      spendingPeriod: value as BankingCardLimitPeriodEnum,
                    })
                  }}
                  readonly={!cardSpendType}
                />
              </Box>

              <Box gap="s8" maxWidth="s600">
                <Text.Normal size="200" weight="medium" color="grey1000">
                  {i18n.t('software_management.initial_setup.wizard.costs_form.flexibility')}
                </Text.Normal>

                <Box gap="s16" flexDirection="row">
                  {flexibilityOptions.map(({ value }) => (
                    <Box width="s80" key={value}>
                      <ToggleButton
                        label={value + '%'}
                        toggled={value == budgetFlexibility}
                        onClick={() => {
                          track(trackingEvents.softwareManagement.selectCardBudgetFlexibility, {
                            value,
                          })
                          setFlexibility(value)
                        }}
                        disabled={!cardSpendType}
                      />
                    </Box>
                  ))}
                </Box>
              </Box>

              {!cardholderExists && (
                <Box gap="s24" width="s400" paddingTop="s24">
                  <Box gap="s8">
                    <Text.Header2>
                      {i18n.t('software_management.initial_setup.wizard.steps.cardholder.title')}
                    </Text.Header2>

                    <Text.Normal>
                      {i18n.t(
                        'software_management.initial_setup.wizard.steps.cardholder.description'
                      )}
                    </Text.Normal>
                  </Box>

                  <DayPicker
                    label={i18n.t(
                      'software_management.initial_setup.wizard.cardholder_form.birth_date'
                    )}
                    onChange={(value) =>
                      onDataChanged({ ...data, birthDate: dateUIToBackend(value) })
                    }
                    value={birthDate ? dateBackendToUI(birthDate) : dateToString(maxDate, 'ui')}
                    maxDate={maxDate}
                    mandatory={!cardholderExists}
                    error={errors?.birthDate}
                    readonly={!cardSpendType}
                  />

                  <Box flexDirection="row" gap="s16">
                    <Box flexGrow>
                      <DialCodeSelector
                        label={i18n.t(
                          'software_management.initial_setup.wizard.cardholder_form.country_code'
                        )}
                        onChange={(value) => onDataChanged({ ...data, countryCode: value })}
                        value={countryCode || ''}
                        locale={locale}
                        mandatory={!cardholderExists}
                        readOnly={!cardSpendType}
                      />
                    </Box>

                    <Box>
                      <TextField
                        label={i18n.t(
                          'software_management.initial_setup.wizard.cardholder_form.phone_number'
                        )}
                        onChange={(value) => onDataChanged({ ...data, phoneNumber: value })}
                        value={phoneNumber + ''}
                        mandatory={!cardholderExists}
                        error={errors?.phoneNumber}
                        readonly={!cardSpendType}
                      />
                    </Box>
                  </Box>
                </Box>
              )}

              <Box paddingTop="s24">
                <Alert>
                  <Box gap="s8" flexDirection="row">
                    <Icon.Medium icon="credit-card" />
                    <Text.Normal>
                      {i18n.tView('software_management.initial_setup.wizard.alerts.card_info_v2', {
                        amount: <Text.Normal weight="medium">{formattedAmount}</Text.Normal>,
                        period: <Text.Normal weight="medium">{spendingPeriod}</Text.Normal>,

                        employee: <Text.Normal weight="medium">{currentUserFullName}</Text.Normal>,
                      })}
                    </Text.Normal>
                  </Box>
                </Alert>
              </Box>
            </Box>
            <Box gap="s48" width="s600" paddingTop="s80">
              <Alert color="info">
                <Text.Normal>
                  {i18n.t('software_management.initial_setup.wizard.alerts.flexibility_info')}
                </Text.Normal>
              </Alert>

              <Alert color="info">
                <Text.Normal>
                  {i18n.t('software_management.initial_setup.wizard.alerts.details_info')}
                </Text.Normal>
              </Alert>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  )
}
